import { MouseEventHandler, useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Popover, Tooltip } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { navigateToTenantSettings } from 'router/navigation';

import { setNotificationPopoverState } from '_common/components/Popovers/NotificationsPopover/NotificationsSlice';
import { selectUserIsAdmin } from '_common/services/api/authority';

import NotificationsPopover from '_common/components/Popovers/NotificationsPopover/NotificationsPopover';
import UserAvatar from '_common/components/UserAvatar/UserAvatar';
import UserPopover from '_common/components/Popovers/UserPopover/UserPopover';
import SupportPopover from './SupportPopover/SupportPopover';

import styles from './Options.module.scss';
import { InteractionController, ToggleMonochrome } from '_common/components';
import { completeAction } from 'App/redux/onboardingSlice';

const Options = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();

  const [notificationsPopoverIsOpen, setNotificationsPopoverIsOpen] = useState(false);

  const [supportPopoverOpen, setSupportPopoverOpen] = useState(false);
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);

  const appLoading = useSelector((state) => state.app.loading.isOpen);
  const userId = useSelector((state) => state.auth.userId);
  const expanded = useSelector((state) => state.sidebar.expanded);
  const currentPage = useSelector((state) => state.app.currentPage);
  const unreadNotifications = useSelector((state) => state.notifications.unreadCount);
  const currentUserIdAdmin = useSelector(selectUserIsAdmin);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'open-notifications') {
      setNotificationsPopoverIsOpen(true);
      //Clear url parameters
      window.history.pushState(null, '', location.pathname);
    }
  }, [location]);

  useEffect(() => {
    dispatch(setNotificationPopoverState(notificationsPopoverIsOpen));
  }, [notificationsPopoverIsOpen]);

  const handleNavigateToTenantSettings: MouseEventHandler<HTMLSpanElement> = () => {
    if (location.pathname !== '/settings') {
      navigateToTenantSettings();
      dispatch(completeAction('explorer_tenant_settings_click'));
    }
  };

  return (
    <div className={styles.root}>
      <ul className={styles.menu}>
        <Popover.Root
          placement="right-end"
          open={notificationsPopoverIsOpen}
          onOpenChange={setNotificationsPopoverIsOpen}
          options={{
            offset: 16,
            size: {
              padding: 40,
            },
          }}
        >
          <Popover.Trigger asChild>
            <li className={styles.menuItemContainer}>
              <Tooltip
                visible={!expanded}
                content={intl.formatMessage({ id: 'NOTIFICATIONS' })}
                placement="right"
                testId="sidebar-notifications"
              >
                <ToggleMonochrome
                  testId="sidebar-notifications"
                  iconProps={{
                    icon: 'bell',
                    badge:
                      unreadNotifications > 0
                        ? {
                            number: unreadNotifications,
                            position: 'top-right',
                            size: 'm',
                          }
                        : undefined,
                  }}
                  loading={appLoading}
                  expanded={expanded}
                  isToggled={notificationsPopoverIsOpen}
                  onClick={() => setNotificationsPopoverIsOpen(!notificationsPopoverIsOpen)}
                  margin="0 0 1rem 0"
                >
                  <FormattedMessage id="NOTIFICATIONS" />
                </ToggleMonochrome>
              </Tooltip>
            </li>
          </Popover.Trigger>
          <Popover.Content
            testId="notifications-option"
            style={{ marginBottom: '5rem', overflow: 'auto' }}
          >
            <NotificationsPopover
              closePopover={() => setNotificationsPopoverIsOpen(false)}
              popperHeight={640}
            />
          </Popover.Content>
        </Popover.Root>
        {currentUserIdAdmin && (
          <li className={styles.menuItemContainer}>
            <Tooltip
              visible={!expanded}
              content={intl.formatMessage({ id: 'global.settings' })}
              placement="right"
              testId="sidebar-settings"
            >
              <span>
                <InteractionController
                  environment="explorer"
                  rules={[{ interaction: 'explorer_tenant_settings' }]}
                >
                  <ToggleMonochrome
                    testId="sidebar-settings"
                    iconProps={{ icon: 'gear' }}
                    onClick={handleNavigateToTenantSettings}
                    loading={appLoading}
                    expanded={expanded}
                    isToggled={currentPage === '/settings'}
                    margin="0 0 1rem 0"
                  >
                    <FormattedMessage id="global.settings" />
                  </ToggleMonochrome>
                </InteractionController>
              </span>
            </Tooltip>
          </li>
        )}
        <Popover.Root
          placement="right-end"
          open={supportPopoverOpen}
          onOpenChange={setSupportPopoverOpen}
        >
          <Popover.Trigger asChild>
            <li className={styles.menuItemContainer} id="SupportPopoverToggle">
              <Tooltip
                visible={!expanded}
                content={intl.formatMessage({ id: 'storage.sidebar.help' })}
                placement="right"
                testId="sidebar-help"
              >
                <ToggleMonochrome
                  testId="sidebar-help"
                  iconProps={{ icon: 'circle-question' }}
                  loading={appLoading}
                  expanded={expanded}
                  isToggled={supportPopoverOpen}
                  margin="0 0 1rem 0"
                  onClick={() => setSupportPopoverOpen(!supportPopoverOpen)}
                >
                  <FormattedMessage id="storage.sidebar.help" />
                </ToggleMonochrome>
              </Tooltip>
            </li>
          </Popover.Trigger>
          <Popover.Content testId="help-option">
            <SupportPopover close={() => setSupportPopoverOpen(false)} />
          </Popover.Content>
        </Popover.Root>
        <Popover.Root
          placement="right-end"
          open={userPopoverOpen}
          onOpenChange={setUserPopoverOpen}
          options={{
            offset: 16,
          }}
        >
          <Popover.Trigger asChild>
            <li className={styles.menuItemContainer} id="UserPopoverToggle">
              <Tooltip
                visible={!expanded}
                content={intl.formatMessage({ id: 'global.profile' })}
                placement="right"
                testId="sidebar-profile"
              >
                <ToggleMonochrome
                  testId="sidebar-profile"
                  loading={appLoading}
                  expanded // Always expanded (DS Toggle.Monochrome only displays icon when not expanded)
                  isToggled={userPopoverOpen || currentPage === '/settings/user'}
                  onClick={() => setUserPopoverOpen(!userPopoverOpen)}
                >
                  <span className={styles.profileToggle} style={{ zIndex: appLoading ? -1 : 0 }}>
                    <UserAvatar size="s" userId={userId} margin={expanded ? '0 0.5rem 0 0' : '0'} />
                    {expanded && <FormattedMessage id="global.profile" />}
                  </span>
                </ToggleMonochrome>
              </Tooltip>
            </li>
          </Popover.Trigger>
          <Popover.Content testId="user-profile-option">
            <UserPopover toggle={() => setUserPopoverOpen(false)} />
          </Popover.Content>
        </Popover.Root>
      </ul>
    </div>
  );
};

export default Options;
